import { createContext, useContext, useEffect, useRef, useState } from "react";
import { SocketContextProps, SocketProviderProps } from "../../interfaces/Sockets.interfaces";
import socketIOClient, { Socket } from "socket.io-client";
import { CustomState } from "../../Hooks/State.hook";
import { currentRoute, LoginContext } from "./LoginContext";
import { toast } from "react-toastify";
import { addPendingQr, getExpiredQrNumbers, PendingQrCodes, removePendingQrs } from "./pendingQrCodes";

function notifyBrowser(body: string) {
    try {
        if (Notification.permission === "granted") new Notification("Autenticação", { body });
    } catch {
        // Some browsers (e.g. Chrome on Android) only allow notifications from a service worker.
    }
};

export const SocketContext = createContext({} as SocketContextProps);

export const SocketProvider = ({ children }: SocketProviderProps) => {

    const socket = CustomState<Socket | null>(null);
    const isConnectedState = CustomState<boolean>(false);
    const oficialMode = CustomState<boolean>(true);
    const isConnected = CustomState<boolean>(false);

    // The pairing itself happens in the "Números WhatsApp" panel; here we only track who is waiting.
    const pendingQrRef = useRef<PendingQrCodes>({});
    const [pendingQrCodes, setPendingQrCodes] = useState<PendingQrCodes>({});
    const [dismissedQrNumbers, setDismissedQrNumbers] = useState<Array<string>>([]);

    const clearPendingQrs = (numbers: Array<string>) => {
        pendingQrRef.current = removePendingQrs(pendingQrRef.current, numbers);
        setPendingQrCodes(pendingQrRef.current);
        setDismissedQrNumbers((current) => current.filter((number) => !numbers.includes(number)));
    };

    const dismissQrNotice = (numbers: Array<string>) => {
        setDismissedQrNumbers((current) => Array.from(new Set([...current, ...numbers])));
    };

    const { user } = useContext(LoginContext);
    const baseURL = process.env.REACT_APP_SOCKET_URL || "http://localhost:8000";

    useEffect(() => {
        const newSocket = socketIOClient(baseURL, {
            transports: ["websocket"],
            withCredentials: true,
            extraHeaders: {
                "ngrok-skip-browser-warning": "skip"
            }
        });

        socket.set(newSocket);

        newSocket.connect()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // A number that stopped sending QR codes (paired elsewhere or offline) leaves the warning.
    useEffect(() => {
        const timer = setInterval(() => {
            const expired = getExpiredQrNumbers(pendingQrRef.current, Date.now());
            if (expired.length) clearPendingQrs(expired);
        }, 15_000);

        return () => clearInterval(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const handleToastError = (data: string) => toast.error(data, { autoClose: 3000 });
        const handleToastSuccess = (data: string) => toast.success(data, { autoClose: 1500 });

        const handleQr = (data: { qr: string, number: string }) => {
            const number = data.number || "";

            // The QR is renewed every few seconds; only a number that starts waiting is announced.
            if (!pendingQrRef.current[number]) {
                notifyBrowser("Um número do WhatsApp está aguardando a leitura do QR code. Abra o menu Números WhatsApp.");
            }

            pendingQrRef.current = addPendingQr(pendingQrRef.current, number, data.qr, Date.now());
            setPendingQrCodes(pendingQrRef.current);
            isConnectedState.set(false);
        };

        const handleAuthenticated = (data?: { number?: string }) => {
            notifyBrowser("WhatsApp autenticado com sucesso.");

            // wwebjs-api announces its QR without the number, so that entry is cleared as well.
            clearPendingQrs([data?.number || "", ""]);
            isConnectedState.set(true);
        };

        const handleOficialMode = (data: boolean) => {
            oficialMode.set(data);
        };

        const handleGroupMessage = (data: any) => {
            console.log("GROUP MESSAGE", data);
        };

        if (socket.value) {
            socket.value.on("toast", handleToastError);
            socket.value.on("toast-error", handleToastError);
            socket.value.on("toast-success", handleToastSuccess);
            socket.value.on("qr", handleQr);
            socket.value.on("authenticated", handleAuthenticated);
            socket.value.on("oficial-mode", handleOficialMode);
            socket.value.on("group_message", handleGroupMessage);
            socket.value.on("disconnect", () => {
                setTimeout(() => {
                    window.location.reload();
                }, 5000);
            })
        }



        return () => {
            if (socket.value) {
                socket.value.off("toast", handleToastError);
                socket.value.off("toast-error", handleToastError);
                socket.value.off("toast-success", handleToastSuccess);
                socket.value.off("qr", handleQr);
                socket.value.off("authenticated", handleAuthenticated);
                socket.value.off("oficial-mode", handleOficialMode);
                socket.value.off("group_message", handleGroupMessage);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [socket.value]);

    useEffect(() => {
        if (socket.value && user) {
            socket.value.emit("session-connect", { clientName: currentRoute, operatorId: user.CODIGO });
            socket.value.emit("join-room", { clientName: currentRoute, operatorId: user.CODIGO });

            isConnected.set(true);

            if (user.NIVEL === "ADMIN") {
                socket.value.emit("join-room", { clientName: currentRoute, operatorId: -1 });
            };

            if (([40, 45].includes(user.CODIGO) && currentRoute === "odim") || currentRoute === "infotec") {
                socket.value.emit("join-room", { clientName: currentRoute, operatorId: currentRoute === "odim" ? 500 : -2 });
            };

        } else if (socket.value && !user) {
            isConnected.set(false);

            socket.value.emit("session-disconnect");
            socket.value.emit("leave-room");
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [socket.value, user]);

    return (
        <SocketContext.Provider value={{
            pendingQrCodes: Object.values(pendingQrCodes),
            dismissedQrNumbers,
            dismissQrNotice,
            isConnectedState,
            socket,
            oficialMode
        }}>
            {children}
        </SocketContext.Provider>
    );
};